import {useTranslation} from 'react-i18next';
import {Center} from '~/components/Center';
import {HorizontalDivider} from '~/components/HorizontalDivider';
import {Paragraph} from '~/components/Paragraph';
import {Title} from '~/components/Title';
import {TitleSection} from '~/components/TitleSection';
import {ImageHeader} from '~/features/shared';
import {AboutPageType} from '~/services/GetPageAboutService';
import {Gallery} from './Gallery';

const certificaciones = [
  {
    src: 'https://cdn.shopify.com/s/files/1/0745/9438/6205/files/nosotros-nom.png?v=1684875010',
    alt: 'Certificación NOM',
  },
  {
    src: 'https://cdn.shopify.com/s/files/1/0745/9438/6205/files/nosotros-ul.png?v=1684875010',
    alt: 'Certificación UL',
  },
  // {
  //   src: 'https://cdn.shopify.com/s/files/1/0745/9438/6205/files/nosotros-iso.png?v=1684875010',
  //   alt: 'Certificación ISO 9001-2008',
  // },
  {
    src: 'https://cdn.shopify.com/s/files/1/0745/9438/6205/files/nosotros-ce.png?v=1684875010',
    alt: 'Certificación CE',
  },
];

export function AboutPage({page}: {page: AboutPageType}) {
  const {t} = useTranslation();

  return (
    <>
      <Center className="w-full lg:px-20 lg:pt-10">
        <ImageHeader
          image={page.headerImage}
          breadcrumb={[
            {
              title: t('inicio'),
              to: '/',
            },
            {
              title: t('nosotros'),
            },
          ]}
          title={t('nosotros')}
        />

        <div className="px-5 sm:px-10">
          <section className="flex flex-col items-center mb-20">
            <TitleSection
              i18nKey="pages:nosotros:title.0"
              className="mb-4 text-center"
            />

            <Title
              i18nKey="pages:nosotros:title.1"
              className="mb-16 text-lg font-normal leading-normal text-center sm:text-4xl max-w-[50rem] text-blue-dark"
            />

            <Gallery images={page.galeria} />
          </section>

          <section>
            <div className="flex flex-col gap-6 items-center mb-10 md:flex-row md:items-start">
              <div className="mb-6">
                <Title
                  i18nKey="historia"
                  className="mb-6 text-2xl leading-normal sm:text-4xl"
                />

                <Paragraph
                  className="mb-8"
                  i18nKey="pages:nosotros:content.0"
                />
                <Paragraph i18nKey="pages:nosotros:content.1" />
              </div>

              <img
                className="object-cover bg-black rounded-3xl"
                style={{
                  width: 350,
                  height: 300,
                }}
                width={350}
                height={300}
                alt={page.imagen1?.image.altText}
                src={page.imagen1?.image.url}
                loading="eager"
              />
            </div>

            <div className="flex flex-col gap-6 items-center mb-10 md:flex-row">
              <img
                className="hidden object-cover bg-black rounded-3xl md:block"
                style={{
                  width: 300,
                  height: 300,
                }}
                width={300}
                height={300}
                alt={page.imagen2?.image.altText}
                src={page.imagen2?.image.url}
                loading="eager"
              />

              <div className="mb-6 md:mb-0 md:ml-6">
                <Paragraph
                  className="mb-8"
                  i18nKey="pages:nosotros:content.2"
                />
                <Paragraph i18nKey="pages:nosotros:content.3" />
              </div>

              <img
                className="object-cover bg-black rounded-3xl md:hidden"
                style={{
                  width: 300,
                  height: 300,
                }}
                width={300}
                height={300}
                alt="Empleado trabajando"
                src="https://cdn.shopify.com/s/files/1/0745/9438/6205/files/nosotros-empleado.png?v=1687043560&width=300&height=300&crop=center"
                loading="eager"
              />
            </div>

            <div>
              <Title
                i18nKey="proposito"
                className="mb-6 text-2xl leading-normal sm:text-4xl"
              />

              <Paragraph i18nKey="pages:nosotros:content.4" />
            </div>
          </section>
        </div>
      </Center>

      <div className="my-16 bg-gray-900">
        <Center className="w-full lg:px-20 lg:pt-10">
          <section className="flex flex-col gap-16 items-center py-20 px-5 sm:px-10 lg:flex-row lg:gap-24 lg:items-start">
            <div className="flex-1">
              <TitleSection i18nKey="blog" className="mb-4 text-blue-400" />
              <Title
                i18nKey="pages:nosotros:blog.title"
                className="mb-6 text-2xl leading-snug text-white sm:text-4xl sm:leading-snug"
              />

              <Paragraph
                className="mb-8 text-gray-300"
                i18nKey="pages:nosotros:blog.content.0"
              />
              <Paragraph
                i18nKey="pages:nosotros:blog.content.1"
                className="text-gray-300"
              />
            </div>

            <div className="overflow-hidden w-full max-w-sm bg-blue-400 rounded-3xl h-[35rem]">
              <video
                onContextMenu={(e) => e.preventDefault()}
                controlsList="nodownload"
                muted
                loop
                playsInline
                controls
                className={`w-full h-full object-cover`}
              >
                <source
                  src="https://cdn.shopify.com/videos/c/o/v/602a07e8a51646e3ba5e6d061efbbb80.mp4"
                  type="video/mp4"
                />
              </video>
            </div>
          </section>
        </Center>
      </div>

      <Center className="w-full lg:px-20 lg:pt-10">
        <div className="px-5 sm:px-10">
          <section className="flex flex-col gap-8 mb-16 md:flex-row">
            <div className="flex-1">
              <Title i18nKey="mision" className="mb-8 text-4xl text-center" />

              <Paragraph i18nKey="pages:nosotros:mision" />
            </div>

            <div className="flex-1">
              <Title i18nKey="vision" className="mb-8 text-4xl text-center" />

              <Paragraph i18nKey="pages:nosotros:vision" />
            </div>

            <div className="flex-1">
              <Title i18nKey="valores" className="mb-8 text-4xl text-center" />

              <Paragraph i18nKey="pages:nosotros:valores" />
            </div>
          </section>

          <HorizontalDivider className="my-16" />

          <section className="mb-24 md:mb-32">
            <Title
              i18nKey="certificaciones"
              className="mb-4 text-3xl leading-normal sm:mb-16 sm:text-4xl"
            />

            <div className="grid grid-cols-2 gap-8 place-items-center md:grid-cols-4">
              {certificaciones.map((c, i) => {
                return (
                  <img
                    key={i}
                    width={200}
                    height={200}
                    className="flex-1"
                    src={c.src + '&width=200&height=200'}
                    alt={c.alt}
                    loading="eager"
                  />
                );
              })}
            </div>
          </section>
        </div>
      </Center>
    </>
  );
}
